import * as THREE from "three";

import {
  ambientFragmentShader,
  ambientVertexShader,
} from "./shader.js";
import { clamp, createSeededRandom } from "./utils.js";

/**
 * Low-cost background star field that adds scale and camera parallax around
 * the hero model. All animation and soft-spark rendering stays on the GPU.
 */
export class AmbientField {
  constructor(
    scene,
    {
      count = 2_400,
      seed = 0x2f91a6c7,
      colorA = "#79dfff",
      colorB = "#a88bff",
    } = {},
  ) {
    this.scene = scene;
    this.count = clamp(Math.round(count), 800, 5_000);
    const random = createSeededRandom(seed);
    const positions = new Float32Array(this.count * 3);
    const randoms = new Float32Array(this.count * 4);

    for (let index = 0; index < this.count; index += 1) {
      const positionOffset = index * 3;
      const randomOffset = index * 4;
      const depth = random();
      const horizontalSpread = 12 + depth * 18;
      const verticalSpread = 7 + depth * 10;

      positions[positionOffset] = (random() - 0.5) * horizontalSpread * 2;
      positions[positionOffset + 1] =
        (random() - 0.5) * verticalSpread * 2;
      positions[positionOffset + 2] = -3.5 - depth * 22;
      randoms[randomOffset] = random();
      randoms[randomOffset + 1] = random();
      randoms[randomOffset + 2] = random();
      randoms[randomOffset + 3] = random();
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3),
    );
    this.geometry.setAttribute(
      "aRandom",
      new THREE.BufferAttribute(randoms, 4),
    );
    this.geometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(0, 0, -14),
      36,
    );

    this.uniforms = {
      uTime: { value: 0 },
      uPixelRatio: { value: 1 },
      uColorA: { value: new THREE.Color(colorA) },
      uColorB: { value: new THREE.Color(colorB) },
      uOpacity: { value: 0.46 },
    };
    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: ambientVertexShader,
      fragmentShader: ambientFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = "AmbientSpectralField";
    this.points.frustumCulled = false;
    this.points.renderOrder = 0;
    this.scene.add(this.points);
  }

  setPixelRatio(value) {
    this.uniforms.uPixelRatio.value = clamp(value, 1, 2);
  }

  setPalette(colorA, colorB = colorA) {
    this.uniforms.uColorA.value.set(colorA);
    this.uniforms.uColorB.value.set(colorB);
  }

  update(time) {
    this.uniforms.uTime.value = time;
  }

  dispose() {
    this.scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}
